import React, { useState, useRef, useEffect } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import article from "./InvestoContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const RetirementGapFinderCalculator = () => {
  const content = article.find((item) => item.id === 8);
  const [activeTab, setActiveTab] = useState("gap");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [retirementStage, setRetirementStage] = useState("accumulation");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Personal Information
    currentAge: "40",
    retirementAge: "65",
    lifeExpectancy: "85",
    currentIncome: "75000",
    
    // Current Savings
    currentSavings: "150000",
    employerPlan: "50000",
    otherAssets: "50000",
    debt: "25000",
    
    // Retirement Goals
    desiredIncome: "60000",
    inflation: "2.5",
    socialSecurity: "2000",
    pension: "1000",
    
    // Investment Strategy
    expectedReturn: "7",
    savingsRate: "15",
    employerMatch: "5",
    riskTolerance: "moderate",
    
    // Additional Factors
    healthcareCosts: "5000",
    longTermCare: "3000",
    inheritance: "0",
    partTimeWork: "0"
  });

  const [gapAnalysis, setGapAnalysis] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "gap") {
      calculateRetirementGap();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateRetirementGap = () => {
    const {
      currentAge,
      retirementAge,
      lifeExpectancy,
      currentIncome,
      currentSavings,
      employerPlan,
      otherAssets,
      debt,
      desiredIncome,
      inflation,
      socialSecurity,
      pension,
      expectedReturn,
      savingsRate,
      employerMatch,
      healthcareCosts,
      longTermCare,
      inheritance,
      partTimeWork
    } = inputs;

    if (!currentAge || !retirementAge || !currentIncome) {
      return;
    }

    const ageNow = parseInt(currentAge);
    const ageRetire = parseInt(retirementAge);
    const ageLife = parseInt(lifeExpectancy);
    const yearsToRetire = ageRetire - ageNow;
    const yearsInRetirement = ageLife - ageRetire;

    // Current financial position
    const totalAssets = parseFloat(currentSavings) + parseFloat(employerPlan) + parseFloat(otherAssets);
    const netWorth = totalAssets - parseFloat(debt);

    // Retirement needs calculation
    const inflationDecimal = parseFloat(inflation) / 100;
    const desiredAnnualIncome = parseFloat(desiredIncome);
    const socialSecurityAnnual = parseFloat(socialSecurity) * 12;
    const pensionAnnual = parseFloat(pension) * 12;
    const partTimeAnnual = parseFloat(partTimeWork) * 12;
    
    // Calculate retirement income gap
    const guaranteedIncome = socialSecurityAnnual + pensionAnnual + partTimeAnnual;
    const annualIncomeGap = desiredAnnualIncome - guaranteedIncome;
    
    // Calculate total retirement needs (25x annual gap for 4% rule)
    const retirementNeeded = annualIncomeGap * 25;
    
    // Factor in additional costs
    const healthcareAnnual = parseFloat(healthcareCosts);
    const longTermCareAnnual = parseFloat(longTermCare);
    const totalAdditionalCosts = (healthcareAnnual + longTermCareAnnual) * yearsInRetirement;
    const totalRetirementNeeded = retirementNeeded + totalAdditionalCosts - parseFloat(inheritance);

    // Project future savings
    const returnRate = parseFloat(expectedReturn) / 100;
    const savingsDecimal = parseFloat(savingsRate) / 100;
    const matchDecimal = parseFloat(employerMatch) / 100;
    const annualSavings = (parseFloat(currentIncome) * savingsDecimal) + (parseFloat(currentIncome) * matchDecimal);
    
    // Project current assets to retirement
    let projectedSavings = totalAssets;
    const savingsGrowth = [];
    
    for (let year = 1; year <= yearsToRetire; year++) {
      projectedSavings = projectedSavings * (1 + returnRate) + annualSavings;
      savingsGrowth.push({
        year: ageNow + year,
        value: projectedSavings,
        contributions: annualSavings * year
      });
    }

    const finalProjectedSavings = projectedSavings;
    const retirementGap = totalRetirementNeeded - finalProjectedSavings;
    const gapPercentage = (retirementGap / totalRetirementNeeded) * 100;
    
    // Calculate required actions
    const requiredSavingsIncrease = retirementGap > 0 ? (retirementGap / (annualSavings * yearsToRetire)) * 100 : 0;
    const requiredReturnIncrease = retirementGap > 0 ? Math.pow(totalRetirementNeeded / (finalProjectedSavings + annualSavings * yearsToRetire), 1/yearsToRetire) - 1 - returnRate : 0;
    const requiredRetirementAge = calculateRequiredRetirementAge(ageNow, finalProjectedSavings, annualSavings, returnRate, totalRetirementNeeded);

    const analysis = {
      // Basic metrics
      yearsToRetirement: yearsToRetire,
      retirementYears: yearsInRetirement,
      currentNetWorth: netWorth,
      
      // Needs analysis
      totalRetirementNeeded: totalRetirementNeeded,
      projectedSavings: finalProjectedSavings,
      retirementGap: retirementGap,
      gapPercentage: Math.abs(gapPercentage),
      
      // Income analysis
      annualIncomeGap: annualIncomeGap,
      guaranteedIncome: guaranteedIncome,
      additionalCosts: totalAdditionalCosts,
      
      // Projections
      savingsProjection: savingsGrowth,
      monthlySavingsNeeded: annualSavings / 12,
      
      // Solutions
      requiredSavingsIncrease: requiredSavingsIncrease,
      requiredReturnIncrease: requiredReturnIncrease * 100,
      requiredRetirementAge: requiredRetirementAge,
      isOnTrack: retirementGap <= 0,
      
      // Detailed breakdown
      breakdown: {
        assets: {
          currentSavings: parseFloat(currentSavings),
          employerPlan: parseFloat(employerPlan),
          otherAssets: parseFloat(otherAssets),
          total: totalAssets
        },
        income: {
          socialSecurity: socialSecurityAnnual,
          pension: pensionAnnual,
          partTime: partTimeAnnual,
          totalGuaranteed: guaranteedIncome
        },
        costs: {
          healthcare: healthcareAnnual,
          longTermCare: longTermCareAnnual,
          annualTotal: healthcareAnnual + longTermCareAnnual
        }
      }
    };

    setGapAnalysis(analysis);
  };

  const calculateRequiredRetirementAge = (currentAge, currentSavings, annualSavings, returnRate, target) => {
    let age = currentAge;
    let savings = currentSavings;
    
    while (savings < target && age < 80) {
      savings = savings * (1 + returnRate) + annualSavings;
      age++;
    }
    
    return age;
  };

  const getGapSeverity = (gapPercentage) => {
    if (gapPercentage <= 10) return { level: "low", color: "#28A745", label: "On Track" };
    if (gapPercentage <= 25) return { level: "moderate", color: "#FFC107", label: "Minor Gap" };
    if (gapPercentage <= 50) return { level: "high", color: "#FF6B6B", label: "Significant Gap" };
    return { level: "critical", color: "#E53E3E", label: "Critical Gap" };
  };

  const getActionPlan = (analysis) => {
    const actions = [];
    
    if (analysis.retirementGap > 0) {
      if (analysis.requiredSavingsIncrease <= 50) {
        actions.push({
          action: "Increase Savings",
          amount: `Increase monthly savings by $${(analysis.monthlySavingsNeeded * analysis.requiredSavingsIncrease / 100).toFixed(0)}`,
          impact: "High",
          priority: 1
        });
      }
      
      if (analysis.requiredReturnIncrease <= 3) {
        actions.push({
          action: "Adjust Investment Strategy",
          amount: `Increase returns by ${analysis.requiredReturnIncrease.toFixed(1)}%`,
          impact: "Medium",
          priority: 2
        });
      }
      
      if (analysis.requiredRetirementAge - parseInt(inputs.retirementAge) <= 5) {
        actions.push({
          action: "Delay Retirement",
          amount: `Work until age ${analysis.requiredRetirementAge}`,
          impact: "High",
          priority: 3
        });
      }
      
      actions.push({
        action: "Reduce Retirement Spending",
        amount: `Reduce annual needs by $${(analysis.retirementGap / 25).toFixed(0)}`,
        impact: "Medium",
        priority: 4
      });
    } else {
      actions.push({
        action: "Maintain Current Plan",
        amount: "You're on track for retirement!",
        impact: "N/A",
        priority: 1
      });
    }
    
    return actions.sort((a, b) => a.priority - b.priority);
  };

  const resetCalculator = () => {
    setInputs({
      currentAge: "40",
      retirementAge: "65",
      lifeExpectancy: "85",
      currentIncome: "75000",
      currentSavings: "150000",
      employerPlan: "50000",
      otherAssets: "50000",
      debt: "25000",
      desiredIncome: "60000",
      inflation: "2.5",
      socialSecurity: "2000",
      pension: "1000",
      expectedReturn: "7",
      savingsRate: "15",
      employerMatch: "5",
      riskTolerance: "moderate",
      healthcareCosts: "5000",
      longTermCare: "3000",
      inheritance: "0",
      partTimeWork: "0"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!gapAnalysis) return;
    
    const csvContent = [
      "Metric,Value",
      `Current Age,${inputs.currentAge}`,
      `Retirement Age,${inputs.retirementAge}`,
      `Years to Retirement,${gapAnalysis.yearsToRetirement}`,
      `Current Net Worth,$${gapAnalysis.currentNetWorth.toFixed(2)}`,
      `Retirement Needs,$${gapAnalysis.totalRetirementNeeded.toFixed(2)}`,
      `Projected Savings,$${gapAnalysis.projectedSavings.toFixed(2)}`,
      `Retirement Gap,$${gapAnalysis.retirementGap.toFixed(2)}`,
      `Gap Percentage,${gapAnalysis.gapPercentage.toFixed(1)}%`,
      `Annual Income Gap,$${gapAnalysis.annualIncomeGap.toFixed(2)}`,
      `Monthly Savings Needed,$${gapAnalysis.monthlySavingsNeeded.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retirement-gap-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!gapAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: gapAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      early: { retirementAge: "55", expectedReturn: "8", savingsRate: "20" },
      standard: { retirementAge: "65", expectedReturn: "7", savingsRate: "15" },
      late: { retirementAge: "70", expectedReturn: "6", savingsRate: "12" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} retirement preset applied`, "info");
  };

  // Chart Data
  const gapChartData = gapAnalysis ? {
    labels: ["Projected Savings", "Retirement Gap"],
    datasets: [{
      data: [
        Math.max(gapAnalysis.projectedSavings, 0),
        Math.max(gapAnalysis.retirementGap, 0)
      ],
      backgroundColor: [
        gapAnalysis.isOnTrack ? "#28A745" : "#19692C",
        "#E53E3E"
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const savingsProjectionData = gapAnalysis ? {
    labels: gapAnalysis.savingsProjection.map(d => `Age ${d.year}`),
    datasets: [
      {
        label: 'Projected Savings',
        data: gapAnalysis.savingsProjection.map(d => d.value),
        borderColor: "#19692C",
        backgroundColor: "rgba(25, 105, 44, 0.1)",
        fill: true,
        tension: 0.4
      },
      {
        label: 'Total Contributions',
        data: gapAnalysis.savingsProjection.map(d => d.contributions),
        borderColor: "#FFD93D",
        backgroundColor: "rgba(255, 217, 61, 0.1)",
        fill: false,
        borderDash: [5, 5],
        tension: 0.4
      }
    ]
  } : null;

  const incomeBreakdownData = gapAnalysis ? {
    labels: ['Social Security', 'Pension', 'Part-time Work', 'Savings Needed'],
    datasets: [{
      data: [
        gapAnalysis.breakdown.income.socialSecurity,
        gapAnalysis.breakdown.income.pension,
        gapAnalysis.breakdown.income.partTime,
        Math.max(gapAnalysis.annualIncomeGap, 0)
      ],
      backgroundColor: ["#2B8C3A", "#28A745", "#1A7C2A", "#FF6B6B"],
      borderWidth: 2
    }]
  } : null;

  const gapSeverity = gapAnalysis ? getGapSeverity(gapAnalysis.gapPercentage) : null;
  const actionPlan = gapAnalysis ? getActionPlan(gapAnalysis) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Retirement Gap Finder</h1>
        </div>
        <p>Identify your retirement savings gap and create a plan to bridge it</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "gap" ? "active" : ""}`}
                onClick={() => setActiveTab("gap")}
              >
                <i className="fas fa-search"></i>
                {isMobile ? 'Gap Analysis' : 'Gap Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "solutions" ? "active" : ""}`}
                onClick={() => setActiveTab("solutions")}
              >
                <i className="fas fa-lightbulb"></i>
                {isMobile ? 'Solutions' : 'Solution Finder'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-bar"></i>
                {isMobile ? 'Scenarios' : 'Scenario Testing'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Retirement Stage Presets */}
          <div className="presets-section">
            <h4>Retirement Timeline:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('early')}>
                Early Retirement
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('standard')}>
                Standard Retirement
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('late')}>
                Late Retirement
              </button>
            </div>
          </div>

          {/* Retirement Stage Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${retirementStage === "accumulation" ? "active" : ""}`}
                onClick={() => setRetirementStage("accumulation")}
              >
                <i className="fas fa-piggy-bank"></i>
                Accumulation Phase
              </button>
              <button 
                className={`mode-btn ${retirementStage === "transition" ? "active" : ""}`}
                onClick={() => setRetirementStage("transition")}
              >
                <i className="fas fa-exchange-alt"></i>
                Transition Phase
              </button>
              <button 
                className={`mode-btn ${retirementStage === "retirement" ? "active" : ""}`}
                onClick={() => setRetirementStage("retirement")}
              >
                <i className="fas fa-umbrella-beach"></i>
                Retirement Phase
              </button>
            </div>
            <div className="mode-description">
              {retirementStage === "accumulation" 
                ? "Building your retirement savings through consistent contributions"
                : retirementStage === "transition"
                ? "Within 5-10 years of retirement, focusing on preservation"
                : "In retirement, managing withdrawals and income streams"
              }
            </div>
          </div>

          {/* Gap Analysis Tab */}
          {activeTab === "gap" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-user"></i>
                    Personal Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Current Age</label>
                      <input 
                        type="number" 
                        name="currentAge" 
                        value={inputs.currentAge} 
                        onChange={handleChange}
                        placeholder="40"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Retirement Age</label>
                      <input 
                        type="number" 
                        name="retirementAge" 
                        value={inputs.retirementAge} 
                        onChange={handleChange}
                        placeholder="65"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-heart"></i> Life Expectancy</label>
                      <input 
                        type="number" 
                        name="lifeExpectancy" 
                        value={inputs.lifeExpectancy} 
                        onChange={handleChange}
                        placeholder="85"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Income</label>
                      <input 
                        type="number" 
                        name="currentIncome" 
                        value={inputs.currentIncome} 
                        onChange={handleChange}
                        placeholder="75000"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-piggy-bank"></i>
                    Current Assets & Liabilities
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-wallet"></i> Current Savings</label>
                      <input 
                        type="number" 
                        name="currentSavings" 
                        value={inputs.currentSavings} 
                        onChange={handleChange}
                        placeholder="150000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-building"></i> Employer Plan (401k)</label>
                      <input 
                        type="number" 
                        name="employerPlan" 
                        value={inputs.employerPlan} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-home"></i> Other Assets</label>
                      <input 
                        type="number" 
                        name="otherAssets" 
                        value={inputs.otherAssets} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-credit-card"></i> Total Debt</label>
                      <input 
                        type="number" 
                        name="debt" 
                        value={inputs.debt} 
                        onChange={handleChange}
                        placeholder="25000"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-bullseye"></i>
                    Retirement Goals
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Desired Retirement Income</label>
                      <input 
                        type="number" 
                        name="desiredIncome" 
                        value={inputs.desiredIncome} 
                        onChange={handleChange}
                        placeholder="60000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Expected Inflation (%)</label>
                      <input 
                        type="number" 
                        name="inflation" 
                        value={inputs.inflation} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Social Security (monthly)</label>
                      <input 
                        type="number" 
                        name="socialSecurity" 
                        value={inputs.socialSecurity} 
                        onChange={handleChange}
                        placeholder="2000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-landmark"></i> Pension (monthly)</label>
                      <input 
                        type="number" 
                        name="pension" 
                        value={inputs.pension} 
                        onChange={handleChange}
                        placeholder="1000"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Strategy
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Expected Annual Return (%)</label>
                      <input 
                        type="number" 
                        name="expectedReturn" 
                        value={inputs.expectedReturn} 
                        onChange={handleChange}
                        placeholder="7"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Savings Rate (%)</label>
                      <input 
                        type="number" 
                        name="savingsRate" 
                        value={inputs.savingsRate} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-handshake"></i> Employer Match (%)</label>
                      <input 
                        type="number" 
                        name="employerMatch" 
                        value={inputs.employerMatch} 
                        onChange={handleChange}
                        placeholder="5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-sliders-h"></i> Risk Tolerance</label>
                      <select name="riskTolerance" value={inputs.riskTolerance} onChange={handleChange}>
                        <option value="conservative">Conservative</option>
                        <option value="moderate">Moderate</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Solutions Tab */}
          {activeTab === "solutions" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Solution Finder
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-stethoscope"></i> Healthcare (Annual)</label>
                    <input 
                      type="number" 
                      name="healthcareCosts" 
                      value={inputs.healthcareCosts} 
                      onChange={handleChange}
                      placeholder="5000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-hospital"></i> Long-term Care (annual)</label>
                    <input 
                      type="number" 
                      name="longTermCare" 
                      value={inputs.longTermCare} 
                      onChange={handleChange}
                      placeholder="3000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-gift"></i> Expected Inheritance</label>
                    <input 
                      type="number" 
                      name="inheritance" 
                      value={inputs.inheritance} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-briefcase"></i> Part-time Work (mo)</label>
                    <input 
                      type="number" 
                      name="partTimeWork" 
                      value={inputs.partTimeWork} 
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.analysis.isOnTrack ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.analysis.isOnTrack ? 'fa-check' : 'fa-exclamation-triangle'}`}></i>
                          {calc.analysis.isOnTrack ? 'On Track' : 'Gap Found'}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-dollar-sign"></i> Gap: ${calc.analysis.retirementGap.toFixed(0)}</span>
                        <span><i className="fas fa-percentage"></i> {calc.analysis.gapPercentage.toFixed(1)}%</span>
                        <span><i className="fas fa-calendar"></i> {calc.analysis.yearsToRetirement} years</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "gap" || activeTab === "solutions" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "gap" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-search"></i>
                    Retirement Gap Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {gapAnalysis ? (
                  <>
                    {/* Gap Status */}
                    <div className="results-grid">
                      <div className={`result-card ${gapAnalysis.isOnTrack ? 'highlight' : ''}`}>
                        <div className={`result-value ${gapAnalysis.isOnTrack ? 'positive' : 'negative'}`}>
                          {gapAnalysis.isOnTrack ? 'On Track' : 'Gap Found'}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bullseye"></i>
                          Retirement Status
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: gapSeverity?.color }}>
                          ${Math.abs(gapAnalysis.retirementGap).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          {gapAnalysis.isOnTrack ? 'Surplus' : 'Retirement Gap'}
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {gapAnalysis.gapPercentage.toFixed(1)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Gap Percentage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {gapAnalysis.yearsToRetirement}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-clock"></i>
                          Years to Retirement
                        </div>
                      </div>
                    </div>

                    {/* Gap Severity */}
                    <div className="risk-summary">
                      <h4>Gap Severity: <span style={{ color: gapSeverity?.color }}>{gapSeverity?.label}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${Math.min(gapAnalysis.gapPercentage, 100)}%`,
                              backgroundColor: gapSeverity?.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: gapSeverity?.color }}>
                          {gapAnalysis.gapPercentage.toFixed(1)}% of retirement needs
                        </div>
                      </div>
                    </div>

                    {/* Detailed Analysis */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-bullseye"></i> Total Retirement Needed:</span>
                        <span>${gapAnalysis.totalRetirementNeeded.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Projected Savings at Retirement:</span>
                        <span>${gapAnalysis.projectedSavings.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Annual Income Gap:</span>
                        <span>${gapAnalysis.annualIncomeGap.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-shield-alt"></i> Guaranteed Income Sources:</span>
                        <span>${gapAnalysis.guaranteedIncome.toLocaleString(undefined, {maximumFractionDigits: 0})}/year</span>
                      </div>
                    </div>

                    {/* Gap Visualization */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Retirement Funding Gap
                      </h3>
                      <Doughnut 
                        data={gapChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Savings Projection */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-line"></i>
                        Savings Projection
                      </h3>
                      <Line 
                        data={savingsProjectionData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: true,
                              position: 'top'
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your retirement details to see gap analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "solutions" && gapAnalysis && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Solution Finder
                </h3>

                {/* Action Plan */}
                <div className="risk-summary">
                  <h4>Recommended Action Plan</h4>
                  <div className="scenario-list">
                    {actionPlan.map((action, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-name">
                          <i className={`fas fa-${action.priority === 1 ? 'star' : 'circle'} fa-xs`}></i>
                          {action.action}
                        </div>
                        <div className="scenario-outcome">{action.amount}</div>
                        <div className="scenario-stats">
                          <span className="impact-badge" style={{ 
                            backgroundColor: action.impact === 'High' ? '#E53E3E' : action.impact === 'Medium' ? '#FFC107' : '#28A745'
                          }}>
                            Impact: {action.impact}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Required Adjustments */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: gapAnalysis.requiredSavingsIncrease > 0 ? '#E53E3E' : '#28A745' }}>
                      {gapAnalysis.requiredSavingsIncrease > 0 ? `+${gapAnalysis.requiredSavingsIncrease.toFixed(1)}%` : 'None'}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-piggy-bank"></i>
                      Savings Increase Needed
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: gapAnalysis.requiredReturnIncrease > 0 ? '#E53E3E' : '#28A745' }}>
                      {gapAnalysis.requiredReturnIncrease > 0 ? `+${gapAnalysis.requiredReturnIncrease.toFixed(1)}%` : 'None'}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Return Increase Needed
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: gapAnalysis.requiredRetirementAge > parseInt(inputs.retirementAge) ? '#E53E3E' : '#28A745' }}>
                      {gapAnalysis.requiredRetirementAge}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-calendar"></i>
                      Required Retirement Age
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${gapAnalysis.monthlySavingsNeeded.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-hand-holding-usd"></i>
                      Current Monthly Savings
                    </div>
                  </div><br/>
                </div>

                {/* Income Breakdown */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-bar"></i>
                    Retirement Income Sources
                  </h3>
                  <Doughnut 
                    data={incomeBreakdownData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: isMobile ? 'top' : 'right'
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `$${context.parsed.toLocaleString(undefined, {maximumFractionDigits: 0})}/year`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === "scenarios" && gapAnalysis && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Testing
                </h3>
                
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Current Plan</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Retirement Age:</span>
                        <span>{inputs.retirementAge}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Final Savings:</span>
                        <span>${gapAnalysis.projectedSavings.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Status:</span>
                        <span style={{ color: gapAnalysis.isOnTrack ? '#28A745' : '#E53E3E' }}>
                          {gapAnalysis.isOnTrack ? 'On Track' : 'Gap'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Boost Savings (+5%)</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Retirement Age:</span>
                        <span>{inputs.retirementAge}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Final Savings:</span>
                        <span>${(gapAnalysis.projectedSavings * 1.15).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Status:</span>
                        <span style={{ color: '#28A745' }}>Improved</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Work 2 More Years</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Retirement Age:</span>
                        <span>{parseInt(inputs.retirementAge) + 2}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Final Savings:</span>
                        <span>${(gapAnalysis.projectedSavings * 1.25).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Status:</span>
                        <span style={{ color: '#28A745' }}>On Track</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default RetirementGapFinderCalculator;